import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, ThemeScope } from './contexts/ThemeContext';
import { SelahRestProvider } from './contexts/SelahRestContext';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import { Home, Mic, Calendar, LayoutDashboard, LogOut, Menu, X, Settings as SettingsIcon } from 'lucide-react';
import Disclaimer from './components/Disclaimer';
import ThemeToggle from './components/ThemeToggle';
import SelahBrandMark from './components/SelahBrandMark';
import ScrollToTop from './components/ScrollToTop';
import TrialRibbon from './components/TrialRibbon';
import AIConsentModal from './components/AIConsentModal';
import IOSDiagnostic from './components/IOSDiagnostic';
import OnboardingSuccess from './components/OnboardingSuccess';
import './App.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Record from './pages/Record';
import History from './pages/History';
import Welcome from './pages/Welcome';
import Settings from './pages/Settings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import Support from './pages/Support';
import DeleteAccount from './pages/DeleteAccount';
import SubscriptionReturn from './pages/SubscriptionReturn';
import SubscriptionCancelled from './pages/SubscriptionCancelled';
import Trademarks from './pages/Trademarks';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--sd-bg)]">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Redirect logged-in users from welcome to dashboard
const WelcomeRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--sd-bg)]">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Welcome />;
};

// Layout with Navigation (authenticated area — Restful Night-Mode enabled)
const AppLayout = ({ children }) => {
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/record', icon: Mic, label: 'Record' },
    { path: '/history', icon: Calendar, label: 'History' },
    { path: '/settings', icon: SettingsIcon, label: 'Settings' },
  ];

  const isActive = (path) => location.pathname === path;

  if (!user) {
    return (
      <div className="app-container flex flex-col min-h-screen">
        <div className="flex-1">{children}</div>
        <Disclaimer />
      </div>
    );
  }

  return (
    <ThemeScope>
      <div className="app-container flex flex-col min-h-screen">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-64 flex-col border-r border-[var(--sd-border)] bg-[var(--sd-surface)] p-6 z-50">
          <div className="mb-8 flex items-center gap-3">
            <SelahBrandMark size={38} sparkleSize={13} />
            <div className="min-w-0">
              <h2 className="text-base leading-tight font-brand text-[var(--sd-fg)]">
                Selah Downloads<span className="text-[var(--sd-primary-dark)] text-[10px] align-top ml-0.5">™</span>
              </h2>
              <p className="text-[10px] text-[var(--sd-muted-fg)] mt-0.5 italic font-scripture">Pause. Listen. Receive.</p>
            </div>
          </div>

          {/* Theme toggle in sidebar */}
          <div className="mb-6">
            <ThemeToggle slot="desktop" />
          </div>

          <nav className="flex-1 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
                    ${
                      isActive(item.path)
                        ? 'bg-[var(--sd-primary)] text-[var(--sd-primary-fg)]'
                        : 'text-[var(--sd-muted-fg)] hover:bg-[var(--sd-surface-hover)] hover:text-[var(--sd-fg)]'
                    }
                  `}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="pt-6 border-t border-[var(--sd-border)]">
            <div className="mb-4">
              <p className="text-sm font-medium truncate text-[var(--sd-fg)]">{user.name}</p>
              <p className="text-xs text-[var(--sd-muted-fg)] truncate">{user.email}</p>
            </div>
            <Button
              onClick={logout}
              variant="ghost"
              className="w-full justify-start text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)]"
              data-testid="logout-button"
            >
              <LogOut size={18} className="mr-2" />
              Sign out
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <div className="lg:ml-64 flex flex-col min-h-screen">
          {/*
            Trial Ribbon — persistent, always-visible subscription disclosure.
            Mounted at the very top of the main content column so it appears
            above both the mobile header and the desktop hero. This is a
            deliberate, non-dismissable disclosure to prevent any perception
            of surprise billing after the 7-day trial ends.
          */}
          <TrialRibbon />

          {/* Mobile header */}
          <header className="lg:hidden sticky top-0 z-40 bg-[var(--sd-bg)] border-b border-[var(--sd-border)] p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <SelahBrandMark size={32} sparkleSize={11} />
                <h2 className="text-lg font-brand text-[var(--sd-fg)] truncate">
                  Selah Downloads<span className="text-[var(--sd-primary-dark)] text-xs align-top ml-0.5">™</span>
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <ThemeToggle compact slot="mobile" />
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 rounded-lg hover:bg-[var(--sd-surface-hover)] text-[var(--sd-fg)]"
                  aria-label="Toggle navigation"
                >
                  {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            </div>

            {/* Mobile menu */}
            {mobileMenuOpen && (
              <div className="mt-4 py-4 border-t border-[var(--sd-border)]">
                <nav className="space-y-2">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`
                          flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
                          ${
                            isActive(item.path)
                              ? 'bg-[var(--sd-primary)] text-[var(--sd-primary-fg)]'
                              : 'text-[var(--sd-muted-fg)] hover:bg-[var(--sd-surface-hover)]'
                          }
                        `}
                      >
                        <Icon size={20} />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </nav>
                <div className="mt-4 pt-4 border-t border-[var(--sd-border)]">
                  <Button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    variant="ghost"
                    className="w-full justify-start text-[var(--sd-muted-fg)]"
                  >
                    <LogOut size={18} className="mr-2" />
                    Sign out
                  </Button>
                </div>
              </div>
            )}
          </header>

          {/* Page content */}
          <main className="flex-1 pb-24 lg:pb-0">{children}</main>

          {/* AI consent modal — blocking on first launch (Apple 5.1.1(i)) */}
          <AIConsentModal />

          {/* One-time celebration when Premium first activates.
              Renders only when user.subscription.is_active is true AND
              user.onboarding.premium_first_seen_at is empty. */}
          <OnboardingSuccess user={user} onDismiss={refreshUser} />

          {/* Disclaimer */}
          <Disclaimer />

          {/* Mobile bottom navigation */}
          <nav className="mobile-nav lg:hidden">
            <div className="flex items-center justify-around">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`mobile-nav-item ${isActive(item.path) ? 'active' : ''}`}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
      </div>
    </ThemeScope>
  );
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <ThemeProvider>
        <AuthProvider>
          <SelahRestProvider>
            <Routes>
            {/* Welcome/Landing page */}
            <Route path="/" element={<WelcomeRoute />} />

            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/record"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Record />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <History />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Settings />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/support" element={<Support />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/subscription/return" element={<SubscriptionReturn />} />
            <Route path="/subscription/cancelled" element={<SubscriptionCancelled />} />
            <Route path="/trademarks" element={<Trademarks />} />

            {/* Default redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* Toast notifications */}
          <Toaster position="top-right" />

          {/* TestFlight / iOS in-app purchase diagnostic overlay.
              Only renders on iOS builds or with ?_debug=1. Safe to remove
              once native IAP is verified in the App Store. */}
          <IOSDiagnostic />
          </SelahRestProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
